import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Crown, Check, X, TrendingUp, Users, ShoppingBag, Euro } from "lucide-react";
import { toast } from "sonner";

const FEATURE_LABELS = {
  analytics_advanced: "Analytics avanzati", sales_data: "Dati sulle vendite", crm: "CRM clienti",
  deals: "Fuma+ Deals", sponsored_missions: "Missioni sponsorizzate", priority_placement: "Posizione prioritaria",
  marketing_tools: "Strumenti marketing", advanced_bookings: "Prenotazioni avanzate", ai_tools: "Strumenti IA",
  reports: "Report", loyalty_tools: "Strumenti loyalty", campaigns: "Gestione campagne", priority_support: "Supporto prioritario",
};

export default function BusinessSubscriptionPanel() {
  const [data, setData] = useState(null);
  const [pro, setPro] = useState(null);
  const [cycle, setCycle] = useState("month");

  const load = () => {
    api.get("/business/subscription").then(({ data }) => { setData(data); setCycle(data.cycle || "month"); }).catch(() => {});
    api.get("/business/pro-savings").then(({ data }) => setPro(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const sess = new URLSearchParams(window.location.search).get("bsub_session");
    if (!sess) return;
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      try { const { data } = await api.get(`/payments/status/${sess}`); if (data.payment_status === "paid") { clearInterval(iv); toast.success("Abbonamento attivato!"); load(); window.history.replaceState({}, "", "/business/subscription"); } } catch { /* noop */ }
      if (n >= 15) { clearInterval(iv); load(); }
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const select = async (plan) => {
    try {
      const { data } = await api.post("/business/subscription/select", { plan, cycle, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.success("Piano aggiornato"); load(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async () => {
    try { await api.post("/business/subscription/cancel"); toast.success("Abbonamento annullato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="text-zinc-500 py-16 text-center">Caricamento…</div>;
  const m = data.metrics || {};
  const eur = (v) => "€" + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-5" data-testid="business-subscription-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Crown className="h-5 w-5 text-[#FFD700]" /> Fuma Business</h2>
        <p className="text-sm text-zinc-500">Il tuo abbonamento e i risultati generati con Fuma questo mese.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[[ShoppingBag, "Ordini (mese)", m.orders_month], [Euro, "GMV (mese)", eur(m.gmv_month)], [TrendingUp, "Revenue Fuma", eur(m.revenue_fuma_month)], [Users, "Clienti (mese)", m.customers_month]].map(([Ic, l, v], i) => (
          <div key={i} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-3">
            <Ic className="h-4 w-4 text-[#00E676] mb-1" />
            <div className="text-lg font-bold">{v ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
          </div>
        ))}
      </div>

      {pro && data.plan !== "pro" && (
        <div data-testid="pro-savings-widget" className="rounded-2xl border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-[#131316] p-5">
          <div className="font-display font-bold text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[#00E676]" /> Ti conviene Fuma Pro?</div>
          {pro.commissionable_gmv > 0 ? (
            <>
              <div className="mt-3 text-sm">
                {[["Transato commissionabile (mese)", eur(pro.commissionable_gmv)],
                  [`Commissioni Start ${(pro.start_rate * 100).toFixed(0)}%`, eur(pro.start_commission)],
                  [`Commissioni Pro ${(pro.pro_rate * 100).toFixed(0)}%`, eur(pro.pro_commission)],
                  ["Abbonamento Pro", eur(pro.pro_price)],
                  ["Costo totale Pro", eur(pro.pro_total_cost)]].map(([l, v], i) => (
                  <div key={i} className="flex justify-between border-b border-white/5 py-1"><span className="text-zinc-400">{l}</span><span className="font-mono">{v}</span></div>
                ))}
              </div>
              <div className={`mt-3 text-lg font-bold ${pro.worth_it ? "text-[#00E676]" : "text-zinc-300"}`} data-testid="pro-savings-result">
                {pro.worth_it ? `Con Pro risparmieresti ${eur(pro.potential_saving)} questo mese` : `Con Pro pagheresti ${eur(-pro.potential_saving)} in più questo mese`}
              </div>
              {!pro.worth_it && pro.breakeven_gmv && <div className="text-xs text-zinc-500 mt-1">Pro conviene oltre ~{eur(pro.breakeven_gmv)} di transato mensile commissionabile.</div>}
            </>
          ) : (
            <div className="text-sm text-zinc-400 mt-2">Con Pro paghi il {((pro.start_rate - pro.pro_rate) * 100).toFixed(0)}% in meno di commissioni ({(pro.pro_rate * 100).toFixed(0)}% invece di {(pro.start_rate * 100).toFixed(0)}%). Break-even a ~{eur(pro.breakeven_gmv)} di transato mensile: appena inizi a vendere ti diremo se conviene, con dati reali e trasparenti.</div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm">Piano attuale: <b className="text-[#FFD700]" data-testid="current-plan">{(data.plans.find((p) => p.id === data.plan) || {}).name || data.plan}</b>
          {data.next_renewal && <span className="text-zinc-500"> · rinnovo {String(data.next_renewal).slice(0, 10)}</span>}</div>
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 ml-auto">
          {[["month", "Mensile"], ["year", "Annuale"]].map(([v, l]) => (
            <button key={v} data-testid={`bsub-cycle-${v}`} onClick={() => setCycle(v)} className={`px-3 py-1 rounded-full text-xs ${cycle === v ? "bg-[#00E676] text-black font-semibold" : "text-zinc-400"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.plans.map((p) => {
          const isCurrent = p.id === data.plan;
          const price = cycle === "year" ? p.price_year : p.price_month;
          return (
            <div key={p.id} data-testid={`bsub-plan-${p.id}`} className={`rounded-2xl border p-5 flex flex-col ${isCurrent ? "border-[#FFD700]/60 bg-gradient-to-br from-[#FFD700]/10 to-[#131316]" : "border-white/10 bg-[#1C1C1F]"}`}>
              <div className="font-display font-bold text-lg">{p.name}</div>
              <div className="text-2xl font-black mt-1">{price > 0 ? eur(price) : "Gratis"}<span className="text-xs text-zinc-500 font-normal">{price > 0 ? (cycle === "year" ? "/anno" : "/mese") : ""}</span></div>
              {p.trial_days > 0 && !isCurrent && p.id !== "start" && <div className="text-xs text-[#00E676] mt-1">{p.trial_days} giorni di prova gratuita</div>}
              <ul className="mt-3 space-y-1 flex-1">
                {Object.keys(FEATURE_LABELS).map((k) => (
                  <li key={k} className="flex items-center gap-2 text-xs text-zinc-300">{p.features?.[k] ? <Check className="h-3.5 w-3.5 text-[#00E676]" /> : <X className="h-3.5 w-3.5 text-zinc-600" />} {FEATURE_LABELS[k]}</li>
                ))}
                {p.commission_discount > 0 && <li className="flex items-center gap-2 text-xs text-[#FFD700]"><Check className="h-3.5 w-3.5" /> Commissioni -{(p.commission_discount * 100).toFixed(0)}%</li>}
              </ul>
              {isCurrent ? (
                p.id === "start"
                  ? <Button disabled className="mt-4 rounded-full opacity-50">Piano attuale</Button>
                  : <Button data-testid={`bsub-cancel`} onClick={cancel} variant="outline" className="mt-4 rounded-full border-white/20">Annulla rinnovo</Button>
              ) : (
                <Button data-testid={`bsub-select-${p.id}`} onClick={() => select(p.id)} className="mt-4 bg-[#00E676] text-black rounded-full font-semibold">{p.id === "start" ? "Passa a Start" : "Attiva"}</Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
